import { DateTime } from 'luxon';

import { ExternalSegmentBackendTypes } from '@/common/enums/external-segments/external-segment-backend-types.enum';
import { DASH } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface ExternalSegmentAPI {
  archived: boolean;
  auto_ingested: boolean;
  enabled: boolean;
  category: string;
  description: string;
  demographic_id: number;
  data_provider_id: number;
  external_id: string;
  externalsegment_id: number;
  location_id: number;
  location?: Location;
  created_on_utc: string;
  modified_on_utc: string;
  name: string;
  reach: number;
  s3_path: string;
  type: string;
  cpm: number;
}

export default class ExternalSegment extends Validatable<ExternalSegment> {
  constructor(
    public archived?: boolean,
    public autoIngested?: boolean,
    public enabled?: boolean,
    public category?: ExternalSegmentBackendTypes,
    public description: string = DASH,
    public demographicId?: number,
    public dataProviderId?: number,
    public externalId?: string,
    public externalSegmentId?: number,
    public locationId?: number,
    public location = null,
    public createdOnUtc?: DateTime,
    public modifiedOnUtc?: DateTime,
    public name?: string,
    public reach?: number,
    public s3Path?: string,
    public type?: string,
    public cpm?: number,
  ) {
    super();
  }

  get country() {
    return this.location?.name;
  }

  get cpmFormatted(): string {
    if (!this.cpm) {
      return DASH;
    }
    return i18n.global.n(this.cpm, 'currency');
  }

  toApi(): ExternalSegmentAPI {
    return {
      archived: this.archived,
      auto_ingested: this.autoIngested,
      enabled: this.enabled,
      category: this.category,
      description: this.description,
      demographic_id: this.demographicId,
      data_provider_id: this.dataProviderId,
      external_id: this.externalId,
      externalsegment_id: this.externalSegmentId,
      location_id: this.locationId,
      created_on_utc: this.createdOnUtc ? this.createdOnUtc.toISO() : null,
      modified_on_utc: this.modifiedOnUtc ? this.modifiedOnUtc.toISO() : null,
      name: this.name,
      reach: this.reach,
      s3_path: this.s3Path,
      type: this.type,
      cpm: this.cpm,
    };
  }

  static fromObject(obj: object) {
    return super.fromObject(obj, new ExternalSegment());
  }

  static fromApi(apiObject: ExternalSegmentAPI) {
    return new ExternalSegment(
      apiObject.archived,
      apiObject.auto_ingested,
      apiObject.enabled,
      apiObject.category?.toUpperCase() as ExternalSegmentBackendTypes,
      apiObject.description,
      apiObject.demographic_id,
      apiObject.data_provider_id,
      apiObject.external_id,
      apiObject.externalsegment_id,
      apiObject.location_id,
      apiObject.location,
      apiObject.created_on_utc ? DateTime.fromISO(apiObject.created_on_utc) : null,
      apiObject.modified_on_utc ? DateTime.fromISO(apiObject.modified_on_utc) : null,
      apiObject.name,
      apiObject.reach,
      apiObject.s3_path,
      apiObject.type,
      apiObject.cpm,
    );
  }

  static get includeAllParameters(): string[] {
    return [
      'archived',
      'auto_ingested',
      'category',
      'created_on_utc',
      'cpm',
      'dataprovider_id',
      'demographic_id',
      'description',
      'enabled',
      'external_id',
      'externalsegment_id',
      'location_id',
      'modified_on_utc',
      'name',
      'reach',
      's3_path',
      'type',
    ];
  }
}
